// Pure market-frame normalization for live HydraMarket frames — the ticker /
// sport / outcome grammar behind the store's sport buckets and selectors.
// Extracted from marketStore.ts (#441). Must stay side-effect free: no store,
// clock, logging, or mutable-state imports; all changing facts arrive as
// arguments.
import type { HydraMarket } from '../clients/HydraClient';
import { isDrawOutcome } from '../lib/kalshiTicker/helpers';
import { splitNflTeams } from '../lib/kalshiTicker/teamNames';
import { classifyPolyMlbExtendedSuffix } from '../lib/kalshiTicker/polyPropsEvents';
import type { Period, Sport } from '../views/ValueDashboard/types';

export interface ParsedTicker {
  source: 'kalshi' | 'polymarket';
  sport: Sport;
  date: string;
  awayCode: string;
  homeCode: string;
  outcomeCode: string;
  side: 'away' | 'home';
  /** 'btts' = both-teams-to-score (soccer Game Lines), a single binary yes/no
   *  market per game/period (outcome code 'BTTS', no team). Surfaced via
   *  getBtts; never enters the spread/total/moneyline selectors.
   *  'advance' = soccer knockout "To Advance" 2-way (team-code yesOutcome, no
   *  DRAW). Tagged so it buckets apart from the 1X2 moneyline (getMultiOutcomeEvents
   *  excludes non-'moneyline' types) and surfaces via getAdvanceGame instead. */
  marketType: 'moneyline' | 'spread' | 'total' | 'series' | 'btts' | 'advance';
  seriesYear?: string;   // only set when marketType === 'series'
  /**
   * Tournament token glued to the year on tennis futures legs
   * (KXWTA-26WIM-MUC → 'WIM'). Only set when marketType === 'series' and
   * the leg ticker carries one. Grouping still trusts the EVENT ticker
   * first — a bare-year leg can belong to a tournament event (KXWTA-26-NOS
   * lists under KXWTA-26WIM) — this is the fallback when hydra sends no
   * event ticker.
   */
  seriesEventSeg?: string;
  /**
   * Set on KX{NBA|NHL|MLB}SERIES-… playoff-series-winner tickers. Surfaces
   * alongside per-day moneylines (marketType='moneyline') so the round-N
   * series sits inline at the top of the moneyline view; the date field
   * is a sort-only sentinel since the market spans many calendar days.
   */
  seriesRound?: number;
  /**
   * Set on Kalshi spread tickers (KX*SPREAD-…-{TEAM}{BUCKET}). The bucket is
   * the minimum *integer* margin the team needs for YES to win, i.e.
   * `STL3` resolves YES when STL wins by 3 or more — covers a (bucket − 0.5)
   * line. Line magnitude = floorStrike when hydra forwards it, else
   * `bucket − 0.5` fallback.
   */
  spreadTeamCode?: string;
  spreadBucket?: number;
  /**
   * Set on Kalshi total tickers (KX*TOTAL-…-{STRIKE}). One ticker per
   * strike. YES resolves when total runs/points reach the strike, so the
   * binary "Over X.5" line is `strike − 0.5`. Line value = floorStrike when
   * forwarded, else `strike − 0.5`.
   */
  totalStrike?: number;
  /**
   * Set on Polymarket spread/total slugs whose suffix carries the line
   * (e.g. `-spread-home-1pt5`, `-total-139pt5`). `pt` → `.` in the slug.
   */
  polyLine?: number;
  polySpreadSide?: 'home' | 'away';
  /**
   * Scope of play. 'full' (default / full game) or a sub-period. NBA 1st half
   * ('1H') is the only period supported today; everything else parses as
   * 'full'. Set from the ticker (Kalshi KXNBA1H… prefix / Polymarket -1h-
   * slug segment). The full-game selectors (getGames / getGameEvents) gate to
   * period === 'full' so period markets never pollute the full-game rows;
   * period lines are surfaced via getGameEvents(…, period) / getPeriodMoneyline.
   */
  period: Period;
  /** True only for the Kalshi 1H winner's TIE outcome (KXNBA1HWINNER-…-TIE),
   *  which is a third moneyline outcome with no team. */
  isTie?: boolean;
}

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

export const SPORT_NORMALIZE: Record<string, Sport> = {
  NBA: 'nba', NFL: 'nfl', WNBA: 'wnba', NHL: 'nhl', CBB: 'cbb', CFB: 'cfb', NCAAF: 'cfb', MLB: 'mlb', ATP: 'atp', WTA: 'wta', UFC: 'ufc',
  nba: 'nba', nfl: 'nfl', wnba: 'wnba', nhl: 'nhl', cbb: 'cbb', cfb: 'cfb', ncaaf: 'cfb', mlb: 'mlb', atp: 'atp', wta: 'wta', ufc: 'ufc',
  ESPORTS: 'esports', BOXING: 'boxing', CRICKET: 'cricket', SOCCER: 'soccer',
  // GOLF (he1, 2026-08-20) is FUTURES-ONLY — Poly-only dateless tournament
  // legs across five tours, with no game identity at all. It is deliberately
  // NOT a SPORT_TABS tab (no game selector can ever return a row for it), but
  // it MUST normalize: useHydra's onMarket calls normalizeSport(market.sport)
  // and an edge feed's `accepts(null)` is false, so an unmapped wire code is
  // dropped at the ingest gate before the store ever sees the frame. That gate
  // is where all ~2.1k golf legs were dying. See FUTURES_ONLY_SPORTS.
  GOLF: 'golf', golf: 'golf',
  // ITF feeds both tours and Polymarket's `itf` slug carries no gender. Map it
  // to atp so parsePolyTicker doesn't drop the market (else it returns null and
  // never enters g1); getGames then routes women's ITF to the wta tab via its
  // Kalshi pair's tour. Hydra's Kalshi ITF is already folded to ATP/WTA.
  itf: 'atp', ITF: 'atp',
  esports: 'esports', boxing: 'boxing', cricket: 'cricket', soccer: 'soccer',
  // Polymarket soccer slugs carry the COMPETITION code in the sport segment
  // (PM-fifwc-… / PM-fif-…), not "soccer". Map the registered competitions
  // onto the soccer tab so parsePolyTicker resolves both the 3-way moneyline
  // and the full-game spread/total slugs. FIFA World Cup (`fifwc`) is the live
  // rollout; International Friendlies (`fif`) is parked server-side but mapped
  // here for parity with the Kalshi series list (UEFA CL is Kalshi-only). The
  // moneyline still keys its outcome off market.yesOutcome in
  // getMultiOutcomeEvents — parsePolyTicker's away-anchored outcomeCode is a
  // lower-priority fallback — so this doesn't change 3-way collapse behavior.
  fifwc: 'soccer', fif: 'soccer',
  // Club competitions (live on the edge 2026-07-31): MLS, UEFA Europa League
  // (`uel`) and UEFA Champions League (`ucl`). NOTE these are three DISTINCT
  // competitions from the Conference League (`KXUECL*`) already listed in the
  // Kalshi series map below — a whitelist written for `uecl` matches none of
  // them. Without these, parsePolyTicker returns null for every club-league
  // Poly slug and the whole venue silently disappears from the row.
  mls: 'soccer', uel: 'soccer', ucl: 'soccer',
};

/** Normalize a hydra uppercase (or lowercase) wire code to a g1 Sport id, or
 *  null when g1 doesn't render it. Exported for the per-feed ownership filter
 *  in useHydra — the hydraedge fan-in routes each market frame by sport. */
export function normalizeSport(code: string): Sport | null {
  return SPORT_NORMALIZE[code] ?? null;
}

function parseKalshiTicker(ticker: string, eventTicker: string): ParsedTicker | null {
  // Playoff series-winner shape: KX{NBA|NHL|MLB}SERIES-{YY}{TEAM1}{TEAM2}R{ROUND}-{OUTCOME}
  // e.g. KXNBASERIES-26PHIBOSR1-BOS. One ticker per outcome team. Surfaces
  // inline at the top of the moneyline view, regardless of the calendar day
  // selected — it's a series-level market with no per-day association.
  // Matched first so it doesn't get caught by the championship match below
  // (which is more permissive on the prefix). Team codes are variable
  // length (NHL has 2- and 3-letter codes; MLB/NBA are 3) so we use the
  // outcome team to determine the split point.
  const seriesRoundMatch = ticker.match(
    /^KX(NBA|WNBA|NHL|MLB)SERIES-(\d{2})([A-Z]+)R(\d)-([A-Z0-9]+)$/,
  );
  if (seriesRoundMatch) {
    const [, sportLetters, year, teamsPart, round, outcomeCode] = seriesRoundMatch;
    const sport = SPORT_NORMALIZE[sportLetters];
    if (sport) {
      // Outcome team must appear at the start or end of the teams chunk;
      // its position picks the split. (Same trick as the per-day parser.)
      let team1: string | null = null;
      let team2: string | null = null;
      if (teamsPart.startsWith(outcomeCode)) {
        team1 = outcomeCode;
        team2 = teamsPart.slice(outcomeCode.length);
      } else if (teamsPart.endsWith(outcomeCode)) {
        team1 = teamsPart.slice(0, teamsPart.length - outcomeCode.length);
        team2 = outcomeCode;
      }
      if (team1 && team2) {
        return {
          source: 'kalshi', sport,
          // Sentinel date: sorts after any real game date so series rows
          // pile at the bottom of the moneyline list (per-day games on top).
          // Round number is the tiebreaker so R1 sits above R2, etc.
          date: `9999-${round}-${year}`,
          awayCode: team1, homeCode: team2,
          outcomeCode,
          side: outcomeCode === team1 ? 'away' : 'home',
          marketType: 'moneyline',
          seriesYear: year,
          seriesRound: parseInt(round, 10),
          period: 'full',
        };
      }
    }
  }

  // Series (championship) shape: KXNBA-26-LAL / KXNHL-26-EDM / KXMLB-26-SEA.
  // Tennis tournament futures carry an optional tournament token glued to the
  // year (KXWTA-26WIM-MUC, KXATP-26USO-ZVE) — same event family also lists
  // bare-year legs (KXWTA-26-NOS), so both shapes must land in one series
  // event. `[A-Z0-9]*` tolerates the token; the SPORT_NORMALIZE gate below
  // still drops non-futures series (KXWTAMATCH-… parses sportLetters
  // 'WTAMATCH' → unknown → falls through to the generic path as before).
  // Matched on the full ticker (no GAME/MATCH/FIGHT suffix), before the generic path.
  const seriesChampMatch = ticker.match(/^KX([A-Z]+)-(\d{2})([A-Z0-9]*)-([A-Z0-9]+)$/);
  if (seriesChampMatch) {
    const [, sportLetters, year, eventSeg, outcomeCode] = seriesChampMatch;
    const sport = SPORT_NORMALIZE[sportLetters];
    // Boxing's per-fight series is the BARE sport code (KXBOXING — no
    // GAME/MATCH/FIGHT suffix), so its fight tickers
    // (KXBOXING-26SEP19FMAYMPAC-FMAY) match this futures shape with
    // sportLetters 'BOXING' — a registered sport, so the SPORT_NORMALIZE gate
    // that drops KXUFCFIGHT/KXMLBGAME here doesn't fire. Distinguish by the
    // segment after the year: a per-day event continues MMMDD…; a futures
    // ticker has either nothing (KXNBA-26-LAL) or a tournament token with no
    // date shape (KXWTA-26WIM-MUC). Dated tickers fall through to the per-day
    // parser below.
    const perDayDated = /^(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\d{2}/.test(eventSeg);
    if (sport && !perDayDated) {
      return {
        source: 'kalshi', sport,
        date: `20${year}-01-01`,
        awayCode: outcomeCode, homeCode: '',
        outcomeCode, side: 'away',
        marketType: 'series',
        seriesYear: year,
        ...(eventSeg ? { seriesEventSeg: eventSeg } : {}),
        period: 'full',
      };
    }
  }

  // Fall back to deriving the event ticker from the ticker itself when hydra
  // didn't send one. moneyline markets reliably carry eventTicker but
  // spread/total frames have been observed without it. Stripping the last
  // dash-segment off the market ticker reproduces the event ticker.
  const derivedEventTicker = eventTicker || ticker.replace(/-[^-]+$/, '');
  // Series names may contain digits (KXCS2GAME, KXDOTA2GAME, KXWT20MATCH) and
  // boxing has no GAME/MATCH/FIGHT suffix (KXBOXING). The sportMap below is the
  // real gate — unknown series fall out there.
  // WINNER is included for NBA 1st-half period moneylines (KXNBA1HWINNER);
  // BTTS for soccer both-teams-to-score; a bare 1H|2H suffix for soccer's
  // period moneyline (KXWC1H/KXWC2H end at the period token — no bet-type after);
  // a bare F5 suffix for MLB's First-5-Innings moneyline (KXMLBF5, likewise
  // no bet-type after); and ADVANCE for the soccer knockout "To Advance" 2-way
  // (KXWCADVANCE-…-{TEAM}, one YES market per team). The `-` anchor forces the
  // right split (KXWC1HSPREAD → SPREAD, not 1H), and the sportMap gate below
  // still drops any non-allowlisted series.
  const seriesMatch = derivedEventTicker.match(/^(KX[A-Z0-9]+(?:GAME|MATCH|FIGHT|SPREAD|TOTAL|WINNER|BTTS|F5|1H|2H|ADVANCE)|KXBOXING)-(.+)$/);
  if (!seriesMatch) return null;
  const [, series, rest] = seriesMatch;

  const sportMap: Record<string, Sport> = {
    KXNBAGAME: 'nba', KXWNBAGAME: 'wnba', KXNHLGAME: 'nhl', KXNCAAMBGAME: 'cbb',
    KXMLBGAME: 'mlb', KXATPMATCH: 'atp', KXWTAMATCH: 'wta',
    // #604 — own Kalshi series for the Challenger circuit; still ordinary atp
    // here (the dashboard splits the circuit by exact series, not by sport).
    KXATPCHALLENGERMATCH: 'atp',
    KXUFCFIGHT: 'ufc',
    KXBOXING: 'boxing',
    KXCS2GAME: 'esports', KXDOTA2GAME: 'esports', KXVALORANTGAME: 'esports', KXLOLGAME: 'esports',
    KXWT20MATCH: 'cricket',
    KXNCAAFGAME: 'cfb', KXNCAAFSPREAD: 'cfb', KXNCAAFTOTAL: 'cfb',
    // NFL — per-game moneyline (KXNFLGAME) is hydraedge Phase 1. Spread/total
    // registered for parity; the gate drops them until they're actually listed.
    KXNFLGAME: 'nfl', KXNFLSPREAD: 'nfl', KXNFLTOTAL: 'nfl',
    // NFL halves — 1H/2H winner, spread, total (the six families the #511 audit
    // proved live on the venue). Bare KXNFL1H/KXNFL2H is the half moneyline, no
    // WINNER suffix, so the seriesMatch regex above matches it on its bare
    // `1H|2H` alternative; the period token is derived below. getGames /
    // getGameEvents gate to period==='full', so halves never enter the
    // full-game rows — they surface through the Game Lines period selectors.
    // Producer-side discovery is HydraEdge #8; until it publishes these the
    // sportMap gate simply never sees them, same as every other parity entry.
    KXNFL1H: 'nfl', KXNFL1HSPREAD: 'nfl', KXNFL1HTOTAL: 'nfl',
    KXNFL2H: 'nfl', KXNFL2HSPREAD: 'nfl', KXNFL2HTOTAL: 'nfl',
    // ITF tennis folds into the ATP (men) / WTA (women) tabs.
    KXITFMATCH: 'atp', KXITFWMATCH: 'wta',
    // Soccer (3-way). Mapped so the internal parser tags sport='soccer', but
    // getGames early-returns for multi-outcome sports — these flow through
    // getMultiOutcomeEvents, not the binary GameRow path.
    KXUECLGAME: 'soccer', KXWCGAME: 'soccer', KXINTLFRIENDLYGAME: 'soccer',
    // Soccer knockout "To Advance" — a 2-way (the two teams, no draw) settled on
    // who advances incl. extra time + penalties. One Kalshi YES market per team
    // (KXWCADVANCE-…-{TEAM}). marketType='advance' (derived below) buckets these
    // APART from the 1X2 (they share its canonicalGameKey); they surface via
    // getAdvanceGame, not the 3-way path. Knockout-only (World Cup R32 onward);
    // UECL parity mirrored for when its knockout rounds list (gate drops unlisted).
    KXWCADVANCE: 'soccer', KXUECLADVANCE: 'soccer',
    // Soccer FULL-GAME spread + total. Each line is an ordinary 2-way binary
    // ("Ivory Coast wins by over 1.5 goals" / "Over 0.5 goals scored"), so they
    // flow through the SAME sport-generic getGameEvents path NBA uses — NOT the
    // 3-way moneyline path. FIFA World Cup (KXWCSPREAD/KXWCTOTAL) is verified
    // live; the friendlies + UECL variants mirror their GAME entries for parity
    // (the sportMap gate silently drops them if Kalshi never lists them).
    KXWCSPREAD: 'soccer', KXWCTOTAL: 'soccer',
    KXUECLSPREAD: 'soccer', KXUECLTOTAL: 'soccer',
    KXINTLFRIENDLYSPREAD: 'soccer', KXINTLFRIENDLYTOTAL: 'soccer',
    // Soccer Game Lines — period (1H/2H) + BTTS. The period token is derived
    // below; getGames / getGameEvents gate to period==='full', so periods never
    // enter the full-game rows — they surface through the period selectors.
    // Period moneyline (KXWC1H/KXWC2H, 3-way incl. TIE) parses via getPeriodMoneyline;
    // BTTS (both teams to score — a binary yes/no per game/period) via getBtts.
    // Kalshi-only in Phase 1 (Poly's halftime-result/btts grammar is a follow-up).
    // WC verified live; UECL/INTLFRIENDLY mirror for parity (gate drops unlisted).
    KXWCBTTS: 'soccer', KXWC1H: 'soccer', KXWC2H: 'soccer',
    KXWC1HSPREAD: 'soccer', KXWC2HSPREAD: 'soccer',
    KXWC1HTOTAL: 'soccer', KXWC2HTOTAL: 'soccer',
    KXWC1HBTTS: 'soccer', KXWC2HBTTS: 'soccer',
    KXUECLBTTS: 'soccer', KXUECL1H: 'soccer', KXUECL2H: 'soccer',
    KXUECL1HSPREAD: 'soccer', KXUECL2HSPREAD: 'soccer',
    KXUECL1HTOTAL: 'soccer', KXUECL2HTOTAL: 'soccer',
    KXUECL1HBTTS: 'soccer', KXUECL2HBTTS: 'soccer',
    KXINTLFRIENDLYBTTS: 'soccer', KXINTLFRIENDLY1H: 'soccer', KXINTLFRIENDLY2H: 'soccer',
    KXINTLFRIENDLY1HSPREAD: 'soccer', KXINTLFRIENDLY2HSPREAD: 'soccer',
    KXINTLFRIENDLY1HTOTAL: 'soccer', KXINTLFRIENDLY2HTOTAL: 'soccer',
    KXINTLFRIENDLY1HBTTS: 'soccer', KXINTLFRIENDLY2HBTTS: 'soccer',
    // Club leagues — MLS, UEFA Europa League (UEL) and UEFA Champions League
    // (UCL), all live on the edge as of 2026-07-31. These are DISTINCT from the
    // Conference League (KXUECL*) listed above: `KXUECL` does not prefix-match
    // `KXUEL`/`KXUCL`, and this map is exact-keyed, so a club-league frame
    // parsed to null and was dropped outright.
    //
    // The 3-way moneyline still rendered while spread/total did not, which is
    // what made this look like a line-parsing bug: getMultiOutcomeEvents keeps
    // markets whose ticker fails to parse (`if (parsed && …) continue`) and
    // groups them off canonicalGameKey, whereas getGameEvents needs
    // parsed.spreadBucket / parsed.totalStrike and drops them.
    KXMLSGAME: 'soccer', KXMLSSPREAD: 'soccer', KXMLSTOTAL: 'soccer', KXMLSBTTS: 'soccer',
    KXMLS1H: 'soccer', KXMLS1HSPREAD: 'soccer', KXMLS1HTOTAL: 'soccer', KXMLS1HBTTS: 'soccer',
    KXUELGAME: 'soccer', KXUELSPREAD: 'soccer', KXUELTOTAL: 'soccer', KXUELBTTS: 'soccer',
    KXUEL1H: 'soccer', KXUEL1HSPREAD: 'soccer', KXUEL1HTOTAL: 'soccer', KXUEL1HBTTS: 'soccer',
    KXUCLGAME: 'soccer', KXUCLSPREAD: 'soccer', KXUCLTOTAL: 'soccer', KXUCLBTTS: 'soccer',
    KXUCL1H: 'soccer', KXUCL1HSPREAD: 'soccer', KXUCL1HTOTAL: 'soccer', KXUCL1HBTTS: 'soccer',
    KXNBASPREAD: 'nba', KXWNBASPREAD: 'wnba', KXNHLSPREAD: 'nhl', KXNCAAMBSPREAD: 'cbb',
    KXMLBSPREAD: 'mlb', KXATPSPREAD: 'atp', KXWTASPREAD: 'wta',
    KXNBATOTAL: 'nba', KXWNBATOTAL: 'wnba', KXNHLTOTAL: 'nhl', KXNCAAMBTOTAL: 'cbb',
    KXMLBTOTAL: 'mlb', KXATPTOTAL: 'atp', KXWTATOTAL: 'wta',
    // NBA period markets — 1st half + single quarters. The period token is
    // derived below; getGames / getGameEvents gate to period==='full' so these
    // never enter the full-game rows — they surface only through the period
    // selectors (Game Lines UI). Quarters are Kalshi-only (no Poly quarter
    // markets); there is no quarter WINNER (Kalshi lists only 1H winner).
    KXNBA1HSPREAD: 'nba', KXNBA1HTOTAL: 'nba', KXNBA1HWINNER: 'nba',
    KXNBA1QSPREAD: 'nba', KXNBA2QSPREAD: 'nba', KXNBA3QSPREAD: 'nba', KXNBA4QSPREAD: 'nba',
    KXNBA1QTOTAL: 'nba', KXNBA2QTOTAL: 'nba', KXNBA3QTOTAL: 'nba', KXNBA4QTOTAL: 'nba',
    // WNBA period markets — 1st half only (Kalshi lists no WNBA quarters).
    KXWNBA1HSPREAD: 'wnba', KXWNBA1HTOTAL: 'wnba', KXWNBA1HWINNER: 'wnba',
    // MLB First 5 Innings (F5) — moneyline (3-way incl. TIE), spread, total. The
    // moneyline series is the BARE KXMLBF5 (no GAME suffix — unlike NBA's
    // KXNBA1HWINNER); spread/total append SPREAD/TOTAL. The period token is
    // derived below; getGames / getGameEvents gate to period==='full' so F5
    // never enters the full-game rows — it surfaces only through the Game Lines
    // period selectors. Kalshi-only (Polymarket lists no inning markets); F5 has
    // no F3/F7 siblings.
    KXMLBF5: 'mlb', KXMLBF5SPREAD: 'mlb', KXMLBF5TOTAL: 'mlb',
  };
  const sport = sportMap[series];
  if (!sport) return null;

  // Period markets. The period token (1H/2H, F5, or 1Q–4Q) sits right before the
  // SPREAD/TOTAL/WINNER/BTTS suffix in the series (KXNBA1QTOTAL, KXNBA1HWINNER,
  // KXWC1HBTTS, KXMLBF5TOTAL) — OR is the whole suffix for a bare period
  // moneyline (soccer's KXWC1H/KXWC2H and MLB's KXMLBF5 end at the period token,
  // no bet-type after). KXNBA1HWINNER is the 3-way period moneyline (outcome is a
  // team OR 'TIE'); it ends in none of SPREAD/TOTAL/BTTS so it falls through to
  // 'moneyline' below, as do bare KXWC1H / KXMLBF5. Quarters never carry
  // WINNER/BTTS. Mirrors hydra's grammar.
  const periodMatch = series.match(/(1H|2H|F5|[1-4]Q)(?:SPREAD|TOTAL|WINNER|BTTS)?$/);
  const period: Period = (periodMatch?.[1] as Period | undefined) ?? 'full';
  const marketType: 'moneyline' | 'spread' | 'total' | 'btts' | 'advance' =
    series.endsWith('ADVANCE') ? 'advance'
      : series.endsWith('SPREAD') ? 'spread'
        : series.endsWith('TOTAL') ? 'total'
          : series.endsWith('BTTS') ? 'btts'
            : 'moneyline';
  // UFC FIGHT, tennis MATCH, and all GAME tickers parse as moneyline (1v1 outcome).

  // MLB tickers embed an HHMM start time between the date and the team codes
  // (e.g. KXMLBGAME-26APR131610HOUSEA-SEA where 1610 = 16:10 ET).
  const dateMatch = rest.match(/^(\d{2})([A-Z]{3})(\d{2})(\d{4})?([A-Z].+)$/);
  if (!dateMatch) return null;
  const [, yy, mmm, dd, , teamsPart] = dateMatch;
  const mm = MONTHS[mmm];
  if (!mm) return null;
  const date = `20${yy}-${mm}-${dd}`;

  const tickerParts = ticker.split('-');
  const outcomeCode = tickerParts[tickerParts.length - 1];
  // For moneyline the outcome IS the team code; for spread the team is the
  // leading letters before the bucket digits; total outcomes are pure
  // digits so we fall back to mid-split. Using the team-only prefix to
  // find position in teamsPart correctly handles variable-length codes
  // like MLB's "AZ" alongside "TEX" (teamsPart "AZTEX" — a naïve ceil/2
  // split would yield "AZT"/"EX", which the canonical override would
  // then mask but leave a broken raw teamCode comparison downstream).
  let teamCandidate: string | null = null;
  if (marketType === 'spread') {
    const m = outcomeCode.match(/^([A-Z]+)\d+$/);
    teamCandidate = m ? m[1] : null;
  } else if (marketType === 'moneyline' || marketType === 'advance') {
    // Advance, like moneyline, has a team-code outcome (KXWCADVANCE-…-BRA) — its
    // position in teamsPart picks away vs home.
    teamCandidate = outcomeCode;
  }
  // Split the packed pair when the outcome can't anchor it: totals carry no
  // team code at all, and a period moneyline's TIE leg names neither side.
  //
  // A midpoint split is only correct when both codes are the same width. NFL
  // has seven 2-char codes (GB, KC, LV, NE, NO, SF, TB), so it breaks whenever
  // the short code is the AWAY team — "KCDEN" splits to "KCD"/"EN", a
  // confidently wrong matchup on roughly a fifth of games. ("DENKC" survives
  // only by luck of the ceil.) Resolve NFL against the team-code registry
  // first, which is the same authority the display parser already uses
  // (splitNflTeams, #357) — the two parsers disagreeing on the same ticker was
  // the root cause. Every other sport keeps the midpoint, which is correct
  // wherever both codes are the same width (NBA DET/PHI, MLB NYY/BAL) — but
  // NOT universally; see below.
  //
  // CFB has the identical hazard and solves it differently, by REQUIRING
  // hydra's canonical pair and failing closed (see parseTicker's isCfbLine).
  // That needs a producer guarantee; the registry needs none, so it also
  // repairs historical frames that predate any stamping.
  //
  // NOT FIXED HERE, and deliberately so — MLB and WNBA share this hazard and
  // are still on the midpoint:
  //   KXMLBTOTAL-26MAY132005AZTEX-3   → "AZT" / "EX"   (AZ; the ticker is a
  //                                     documented real one, see MLB_TEAM_NAMES)
  //   KXWNBA1HTOTAL-26JUN05LVCHI-84   → "LVC" / "HI"   (LV/GS/LA/NY/CONN)
  // Both are pre-existing and out of #543's scope, which is NFL halves. WNBA is
  // the cheap one — splitWnbaTeams already exists in teamNames.ts and is unused
  // here. MLB needs more than a registry swap: its packed pair can carry a
  // doubleheader suffix (KXMLBTOTAL-…PITNYYG1-9 already mis-splits to
  // "PITN"/"YYG1"), which the display parser strips and this one does not.
  const splitPacked = (pair: string): [string, string] => {
    if (sport === 'nfl') {
      const fromRegistry = splitNflTeams(pair);
      if (fromRegistry) return fromRegistry;
    }
    const mid = Math.ceil(pair.length / 2);
    return [pair.slice(0, mid), pair.slice(mid)];
  };

  let awayCode: string, homeCode: string;
  if (teamCandidate) {
    const idx = teamsPart.indexOf(teamCandidate);
    if (idx === 0) {
      awayCode = teamCandidate;
      homeCode = teamsPart.slice(teamCandidate.length);
    } else if (idx > 0) {
      awayCode = teamsPart.slice(0, idx);
      homeCode = teamCandidate;
    } else {
      [awayCode, homeCode] = splitPacked(teamsPart);
    }
  } else {
    // Total — no team in the outcome.
    [awayCode, homeCode] = splitPacked(teamsPart);
  }
  if (!awayCode || !homeCode) return null;

  // Determine side: moneyline outcomes are exact team codes (DET),
  // spread outcomes are team+number (DET1, MIA33), totals are just numbers (215).
  // Spread/total tickers also expose the bucket/strike so getGameEvents can
  // keep ALL per-line tickers per game (getGames collapses them — preserved
  // for backwards-compat with the moneyline path which doesn't need per-line).
  let side: 'away' | 'home';
  let spreadTeamCode: string | undefined;
  let spreadBucket: number | undefined;
  let totalStrike: number | undefined;
  if (marketType === 'total') {
    side = 'away'; // totals aren't team-specific
    const totalMatch = outcomeCode.match(/^(\d+)$/);
    if (totalMatch) totalStrike = Number(totalMatch[1]);
  } else if (marketType === 'btts') {
    side = 'away'; // BTTS is one binary (both teams score) — no team/side/strike.
  } else if (outcomeCode === awayCode) {
    side = 'away';
  } else if (outcomeCode === homeCode) {
    side = 'home';
  } else {
    // Spread: outcome is {TEAM}{BUCKET} like "DET7" / "NYK11" / "KC3".
    const spreadMatch = outcomeCode.match(/^([A-Z]+)(\d+)$/);
    if (spreadMatch) {
      spreadTeamCode = spreadMatch[1];
      spreadBucket = Number(spreadMatch[2]);
    }
    side = (spreadTeamCode ?? outcomeCode).startsWith(awayCode) ? 'away' : 'home';
  }

  return {
    source: 'kalshi', sport, date,
    awayCode: awayCode.toUpperCase(), homeCode: homeCode.toUpperCase(),
    outcomeCode: outcomeCode.toUpperCase(),
    side,
    marketType,
    spreadTeamCode,
    spreadBucket,
    totalStrike,
    period,
    // The TIE outcome is the 3rd leg of a 3-way period moneyline —
    // KXNBA1HWINNER-…-TIE, soccer's KXWC1H/KXWC2H-…-TIE, or MLB's
    // KXMLBF5-…-TIE — no team; getPeriodMoneyline keys on this to place the
    // Tie row.
    isTie: (period === '1H' || period === '2H' || period === 'F5') && marketType === 'moneyline' && outcomeCode.toUpperCase() === 'TIE'
      ? true
      : undefined,
  };
}

function parsePolyTicker(ticker: string): ParsedTicker | null {
  const match = ticker.match(/^PM-([a-z]+)-([a-z]+)-([a-z]+)-(\d{4}-\d{2}-\d{2})(.*)$/);
  if (!match) return null;
  const [, sportRaw, awayRaw, homeRaw, date, suffix] = match;
  const sport = SPORT_NORMALIZE[sportRaw];
  if (!sport) return null;
  // Period markets (Game Lines feature). Hydra flows these on the same feed —
  // e.g. `PM-nba-nyk-sas-2026-06-03-1h-moneyline` / `-1h-spread-home-2pt5` —
  // carrying the FULL-GAME canonicalGameKey, with the period token right after
  // the date. Detect a leading `-1h-` segment → period '1H' and parse the rest
  // (-spread/-total/-moneyline) past it. Unsupported periods (2nd half,
  // quarters) are still dropped so they can't mis-parse as a full-game
  // moneyline and clobber g.polyTicker (the 0cf7549 bug). The full-game
  // selectors gate to period==='full', so 1H never pollutes the full-game row.
  // NOTE: quarters (1q–4q) stay dropped *by design* — Polymarket lists no
  // quarter markets, so this branch only ever sees the irregular-slug case as a
  // defensive backstop; quarter coverage is intentionally Kalshi-only.
  let period: Period = 'full';
  let body = suffix;
  // MLB Poly props/events (poly-props-events, 2026-07-16): `-nrfi`,
  // `-extra-innings`, `-{hr|k}-{player}-{XptY}` ride the same PM game stem
  // but are NOT game lines. Drop them here — mirroring the Kalshi side,
  // whose 11 prop/event series never enter this parser's selectors — so the
  // moneyline fall-through below can't claim them as full-game moneylines
  // and clobber the game row (the 0cf7549 class). getMlbPlayerProps /
  // getMlbEventMarkets classify them directly from the ticker instead.
  if (sport === 'mlb' && classifyPolyMlbExtendedSuffix(suffix)) return null;
  const periodMatch = suffix.match(/^-(1h|2h|f5|[1-4]q)\b/);
  if (periodMatch) {
    // -f5- (MLB First 5 Innings) mirrors hydra's own parseGameShape: strip
    // the token so the -spread-/-total- routing below matches, carry the
    // period out. Without this the token slipped the guard and every Poly
    // F5 ticker parsed as a FULL-GAME moneyline sharing the game's cgk —
    // the 0cf7549 clobber class (fed the 2026-07-07 zombie KC/NYM row).
    if (periodMatch[1] === '1h') period = '1H';
    else if (periodMatch[1] === 'f5') period = 'F5';
    else return null; // 2H / quarters not on Polymarket
    body = suffix.slice(periodMatch[0].length); // strip "-1h"/"-f5" → "-spread-…" / "-total-…" / "-moneyline"
  }
  const awayCode = codeFromPolySlug(awayRaw, sport);
  const homeCode = codeFromPolySlug(homeRaw, sport);

  // ── Soccer Game Lines: period (1H/2H) result + totals, and BTTS ──────
  // Poly lists these in separate Gamma events with the scope baked into the
  // bet-type segment (not a -1h-/-2h- token), all sharing the game's
  // canonicalGameKey. Map them so getPeriodMoneyline / getGameEvents(period) /
  // getBtts collapse the Poly book onto the matching Kalshi line. Full-game
  // 1X2 (-can/-qat/-draw), -spread, -total fall through to the logic below.
  if (sport === 'soccer') {
    // Knockout "To Advance" — a SINGLE binary market (`-team-to-advance`) whose
    // YES = the first slug team (= our awayCode). The other team is this same
    // market's NO complement, so only the YES team carries a poly ticker; the
    // getter (getAdvanceGame) splits it onto away/home. yesOutcome (hydra) is
    // authoritative for the YES team — this away-anchored outcomeCode is the
    // lower-priority fallback. marketType='advance' buckets it apart from the 1X2.
    if (suffix === '-team-to-advance') {
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: awayCode, side: 'away', marketType: 'advance', period: 'full',
      };
    }
    // BTTS — one binary per scope. Full game `-btts`, halves `-btts-first-half`
    // / `-btts-second-half`. (Also fixes full-game `-btts` previously parsing as
    // a moneyline and polluting the 3-way.)
    if (suffix === '-btts' || suffix === '-btts-first-half' || suffix === '-btts-second-half') {
      const p: Period = suffix === '-btts-first-half' ? '1H' : suffix === '-btts-second-half' ? '2H' : 'full';
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: 'BTTS', side: 'away', marketType: 'btts', period: p,
      };
    }
    // 3-way half result: `-halftime-result-{away|home|draw}` (1H),
    // `-second-half-result-{away|home|draw}` (2H). Resolve to a TEAM code by slug
    // POSITION so getPeriodMoneyline pairs by code (the Poly↔Kalshi home/away
    // label disagreement then doesn't matter). NOTE: verified live across games,
    // Poly's result `-home` is the FIRST slug team (t1 = our awayCode) and
    // `-away` is the second (t2 = homeCode) — the opposite of the label. (Alias
    // countries like Poly che ↔ Kalshi SUI are a known gap — codes won't match.)
    const resultMatch = suffix.match(/^-(halftime|second-half)-result-(away|home|draw)$/);
    if (resultMatch) {
      const p: Period = resultMatch[1] === 'halftime' ? '1H' : '2H';
      const role = resultMatch[2];
      const isTie = role === 'draw';
      const teamCode = role === 'home' ? awayCode : homeCode; // home→t1, away→t2
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: isTie ? 'DRAW' : teamCode,
        side: isTie || teamCode === awayCode ? 'away' : 'home',
        marketType: 'moneyline', period: p,
        isTie: isTie ? true : undefined,
      };
    }
    // Half totals: `-first-half-total-Npt5` (1H), `-second-half-total-Npt5` (2H).
    const halfTotal = suffix.match(/^-(first|second)-half-total-(\d+(?:pt\d+)?)$/);
    if (halfTotal) {
      const p: Period = halfTotal[1] === 'first' ? '1H' : '2H';
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: awayCode, side: 'away',
        marketType: 'total', period: p,
        polyLine: Number(halfTotal[2].replace('pt', '.')),
      };
    }
  }

  let marketType: 'moneyline' | 'spread' | 'total' = 'moneyline';
  let polyLine: number | undefined;
  let polySpreadSide: 'home' | 'away' | undefined;
  if (body.startsWith('-spread')) {
    marketType = 'spread';
    // `-spread-home-1pt5` or `-spread-away-1pt5`. `pt` is Polymarket's
    // slug-safe stand-in for the decimal point.
    const spreadMatch = body.match(/^-spread-(home|away)-(\d+(?:pt\d+)?)/);
    if (spreadMatch) {
      polySpreadSide = spreadMatch[1] as 'home' | 'away';
      // Polymarket labels SOCCER spreads with the first slug team as "home"
      // (neutral-venue convention) — the inverse of Kalshi/g1's first=away, so
      // its `-spread-home` book is actually the g1 AWAY team's spread, and the
      // side===favored pairing in getGameEvents would attach it to the wrong
      // row. US sports align (MLB `-spread-home` = the home team), so flip
      // soccer only. Verified live: SCO/MAR inverted, BOS/SEA aligned.
      if (sport === 'soccer') polySpreadSide = polySpreadSide === 'home' ? 'away' : 'home';
      polyLine = Number(spreadMatch[2].replace('pt', '.'));
    }
  } else if (body.startsWith('-total')) {
    marketType = 'total';
    // `-total-139pt5` → 139.5
    const totalMatch = body.match(/^-total-(\d+(?:pt\d+)?)/);
    if (totalMatch) polyLine = Number(totalMatch[1].replace('pt', '.'));
  } else if (sport === 'mlb' && body !== '' && body !== '-moneyline') {
    // MLB unknown-suffix FAIL-CLOSED (DNL-G3): anything not a recognized
    // bare/`-moneyline` ML, `-spread-…`, or `-total-…` — a MALFORMED
    // props/events slug the classifier above rejected (`-hr-aj-ewing` with
    // no line token), the un-ingested `-team-total-…`, the uncontracted
    // `-first-five-winner-…`, or any future shape — must never default to
    // a full-game moneyline sharing the game's cgk (the 0cf7549 clobber
    // class). Other sports keep the pre-existing permissive fallback;
    // tightening them is a separate, deliberate change.
    return null;
  }
  // body === '-moneyline' (or '') → marketType stays 'moneyline'.

  return {
    source: 'polymarket', sport, date,
    awayCode, homeCode,
    outcomeCode: awayCode, side: 'away',
    marketType,
    polyLine,
    polySpreadSide,
    period,
  };
}

/**
 * Seed code for one competitor segment of a Polymarket game slug.
 *
 * This value is a FALLBACK, not an identity. Whenever hydra stamps the frame,
 * pass 1 replaces it with `canonicalTeams` and the row groups on hydra's key —
 * full, Title-cased surnames for tennis and (since 2026-07-30) UFC and boxing
 * alike. It survives only on UNSTAMPED frames, where it is also the only thing
 * that can pair the two venues, via the locally-derived
 * `sport|date|away|home` fallback key.
 *
 * Tennis truncates to three letters because that fallback join has to line a
 * Poly slug (`berrett`) up against a Kalshi ticker code (`BER`), and no
 * per-player registry exists to do it properly.
 *
 * UFC was truncated here too (poly-ufc-pairing) on the same reasoning, and is
 * REMOVED as of hydra's 2026-07-30 fight-sport change. Both venues now derive
 * fight keys from full names — Kalshi from the event title, Poly from the
 * market outcomes — so three-letter folding no longer buys a join; it only
 * re-imports the collision the canonical key space just escaped. And it is
 * now actively unsafe: when hydra WITHHOLDS a fight's key (two distinct
 * fights behind one identity — ambiguity fails closed), the pair must stay
 * unpaired rather than re-pair on a lossy local guess, which is exactly what
 * a truncated fallback key would do.
 */
function codeFromPolySlug(raw: string, sport: Sport): string {
  const upper = raw.toUpperCase();
  if (sport === 'atp' || sport === 'wta') return upper.slice(0, 3);
  return upper;
}

export function parseTicker(market: HydraMarket): ParsedTicker | null {
  if (market.source === 'polymarket' || market.ticker.startsWith('PM-')) {
    const parsed = parsePolyTicker(market.ticker);
    // Hydra stamps `period` on poly frames (HYDRA_API contract — 'F5' since
    // the MLB First-5 discovery, 699be32 hydra-side). The frame field is
    // AUTHORITATIVE over the slug-derived period; the slug parse remains the
    // fallback for pre-stamp frames. Quarters stay dropped either way
    // (parsePolyTicker returns null for them before this override runs).
    if (parsed && market.period && market.period !== parsed.period) {
      return { ...parsed, period: market.period };
    }
    return parsed;
  }
  // Series-winner and championship futures don't necessarily carry an
  // eventTicker from hydra, so fall through to the Kalshi parser as long
  // as it's a KX-prefixed ticker. The per-day game branch inside still
  // requires eventTicker; the SERIES / KXNBA-{YY}-{TEAM} branches don't.
  if (market.ticker.startsWith('KX')) {
    const parsed = parseKalshiTicker(market.ticker, market.eventTicker ?? '');
    const isCfbLine = /^KXNCAAF(?:SPREAD|TOTAL)(?:-|$)/.test(
      market.eventTicker || market.ticker,
    );
    // CFB codes are variable-width and totals have no outcome-team suffix to
    // anchor the packed pair. Hydra verifies Kalshi's event-level code pair
    // before stamping these fields. Both fields are mandatory for these two
    // families: without them the old parser would midpoint-split a packed pair
    // such as SJSUUSC into SJS/UUSC, so fail closed instead.
    if (isCfbLine) {
      if (!parsed || !market.canonicalAwayCode || !market.canonicalHomeCode) {
        return null;
      }
      return {
        ...parsed,
        awayCode: market.canonicalAwayCode.toUpperCase(),
        homeCode: market.canonicalHomeCode.toUpperCase(),
      };
    }
    return parsed;
  }
  return null;
}

/** Fold a producer-violating `canonicalTeams` OBJECT (`{away, home}` —
 *  hydracore's manifest shape, observed on hc1's D10 floor frames 2026-08-18)
 *  into the consumer-wire contract: the alpha-sorted `[string, string]` tuple,
 *  recovering orientation into canonicalAwayCode/canonicalHomeCode when the
 *  frame lacks them (core floor frames carry neither). Any other non-tuple
 *  shape is stripped: a malformed frame must degrade to "no canonical teams",
 *  never throw — `canonicalTeams.includes` on the object white-screened the
 *  app on a hard game-URL load. Mutates in place; ingestMarket stores the
 *  frame wholesale, so this must run before the store write. */
export function coerceCanonicalTeams(market: HydraMarket): void {
  const ct = market.canonicalTeams as unknown;
  if (ct == null || Array.isArray(ct)) return;
  if (typeof ct === 'object') {
    const { away, home } = ct as { away?: unknown; home?: unknown };
    if (typeof away === 'string' && away && typeof home === 'string' && home) {
      market.canonicalTeams = [away, home].sort() as [string, string];
      market.canonicalAwayCode ??= away;
      market.canonicalHomeCode ??= home;
      return;
    }
  }
  delete market.canonicalTeams;
}

export function normalizeOutcomeCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  if (!upper) return null;
  return isDrawOutcome(upper) ? 'DRAW' : upper;
}

/**
 * Canonical team/outcome code hydra reconciles onto a Polymarket soccer frame's
 * `yesOutcome` — e.g. it maps the slug spelling `kr` to the FIFA code `KOR`, so
 * period markets pair by code instead of the alias-prone slug. Returns null when
 * the frame still carries a positional value (`HOME`/`AWAY`, pre-reconcile) or
 * none, so callers fall back to the slug-derived parse. 'DRAW' passes through.
 */
export function polyCanonicalOutcome(m: HydraMarket): string | null {
  const yo = normalizeOutcomeCode(m.yesOutcome);
  if (yo && yo !== 'HOME' && yo !== 'AWAY') return yo;
  return normalizeOutcomeCode(m.canonicalYesTeam);
}

export function sportFromMarket(market: HydraMarket, parsed: ParsedTicker | null): Sport | null {
  if (parsed?.sport) return parsed.sport;
  const key = market.canonicalGameKey ?? market.canonicalSeriesKey ?? null;
  if (key) {
    const i = key.indexOf('|');
    if (i > 0) {
      const fromKey = SPORT_NORMALIZE[key.slice(0, i)];
      if (fromKey) return fromKey;
    }
  }
  return market.sport ? (SPORT_NORMALIZE[market.sport] ?? null) : null;
}

/**
 * Every normalized sport bucket a market may need to appear in. Designed as a
 * SUPERSET of how the selectors classify sport, because each selector re-checks
 * sport itself — so a false positive here only costs iterations, a false
 * negative drops a row. Covers, in order: the parsed-ticker sport (getGames
 * per-day / getGameEvents / getSeries / getMultiOutcomeEvents); the
 * `canonicalSeriesKey` sport (getGames' series path keys off this, NOT
 * `market.sport`); the `canonicalGameKey` prefix and raw `market.sport`
 * (sportFromMarket's fallbacks, used by getMultiOutcomeEvents). Finally, any
 * tennis-touching market is filed under BOTH tours: a Polymarket itf slug
 * parses to atp by default but getGames may re-route it to wta via the paired
 * Kalshi side (tennisTourByKey), and the canonical key for tennis is the
 * tour-less `tennis|…`.
 */
export function marketSportKeys(market: HydraMarket): Sport[] {
  const keys = new Set<Sport>();
  const parsed = parseTicker(market);
  if (parsed?.sport) keys.add(parsed.sport);
  if (market.canonicalSeriesKey) {
    const cskSport = SPORT_NORMALIZE[market.canonicalSeriesKey.split('|')[0]];
    if (cskSport) keys.add(cskSport);
  }
  if (market.canonicalGameKey) {
    const i = market.canonicalGameKey.indexOf('|');
    if (i > 0) {
      const ckSport = SPORT_NORMALIZE[market.canonicalGameKey.slice(0, i)];
      if (ckSport) keys.add(ckSport);
    }
  }
  if (market.sport) {
    const ms = SPORT_NORMALIZE[market.sport];
    if (ms) keys.add(ms);
  }
  // Tennis tour ambiguity — mirror getGames' tennisTourByKey fallback so both
  // tabs can find a frame that resolves to the other tour at scan time.
  if (keys.has('atp') || keys.has('wta') || market.canonicalGameKey?.startsWith('tennis|')) {
    keys.add('atp');
    keys.add('wta');
  }
  return Array.from(keys);
}

/** Best tab-sport for a market frame, reusing the same normalization the
 *  sport buckets use (SPORT_NORMALIZE + ticker parse + canonical keys +
 *  the tennis tour fallback). For consumers that need ONE sport for a
 *  frame (e.g. describePolyOrder's frame fallback labeling spread/total
 *  tokens) rather than the bucket set. Null when nothing normalizes. */
export function sportForMarketFrame(market: HydraMarket): Sport | null {
  return marketSportKeys(market)[0] ?? null;
}
